import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Lock, Unlock, Megaphone, Eraser } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const REGIONS = ["Bern", "Basel", "Lausanne"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const AnnounceModerationPanel = ({ onLogout }) => {
  const [region, setRegion] = useState("Bern");
  const [state, setState] = useState({ messages: [], locked: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/announcements/${region}`, { headers: authHeader() });
      setState({ messages: data.messages, locked: data.locked });
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [region, onLogout]);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => load(false), 3000);
    return () => clearInterval(iv);
  }, [load]);

  const toggleLock = async () => {
    setBusy(true);
    try {
      const next = !state.locked;
      await axios.put(`${API}/admin/announcements/${region}/lock?locked=${next}`, {}, { headers: authHeader() });
      setState((s) => ({ ...s, locked: next }));
      toast.success(next ? `Kanal ${region} gesperrt.` : `Kanal ${region} entsperrt.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const removeMsg = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/messages/${id}`, { headers: authHeader() });
      setState((s) => ({ ...s, messages: s.messages.filter((m) => m.id !== id) }));
      toast.success("Ankündigung gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const clearChannel = async () => {
    try {
      const { data } = await axios.delete(`${API}/admin/announcements/${region}/clear`, { headers: authHeader() });
      setState((s) => ({ ...s, messages: [] }));
      toast.success(`${data.deleted_count} Ankündigung(en) gelöscht.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Leeren fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="announce-mod-panel">
      <div className="mb-8">
        <span className="section-eyebrow">Moderation</span>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Ankündigungs-Verwaltung</h1>
        <p className="mt-2 text-sm text-cream/60">Alle Ankündigungs-Kanäle einsehen, Beiträge löschen, Kanal sperren/entsperren.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-2" data-testid="announce-region-tabs">
          {REGIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              data-testid={`announce-region-${r}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                region === r ? "bg-gold text-anthracite border-gold" : "border-gold/25 text-cream/70 hover:text-cream"
              }`}
            >
              Kreis {r}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="announce-clear"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-white/15 text-cream/80 hover:bg-white/10 transition-colors"
              >
                <Eraser size={16} /> Kanal leeren
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-cream">Kanal {region} leeren?</AlertDialogTitle>
                <AlertDialogDescription className="text-cream/60">
                  Damit werden <span className="text-cream/90 font-medium">ALLE Ankündigungen</span> in diesem Kanal dauerhaft gelöscht. Dies kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                <AlertDialogAction data-testid="announce-clear-confirm" onClick={clearChannel} className="bg-red-600 text-white hover:bg-red-700">Alles löschen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button
            onClick={toggleLock}
            disabled={busy}
            data-testid="announce-lock-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
              state.locked ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : state.locked ? <Unlock size={16} /> : <Lock size={16} />}
            {state.locked ? "Kanal entsperren" : "Kanal sperren"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gold/25 bg-gold/[0.03]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gold/20">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cream/80"><Megaphone size={16} style={{ color: "#E5C76B" }} /> Ankündigungen {region}</span>
          {state.locked && <span className="inline-flex items-center gap-1.5 text-xs text-red-400"><Lock size={13} /> Gesperrt</span>}
        </div>

        <div ref={scrollRef} className="max-h-[520px] overflow-y-auto p-4 space-y-2" data-testid="announce-mod-messages">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-cream/50"><Loader2 size={24} className="animate-spin" /></div>
          ) : state.messages.length === 0 ? (
            <p className="text-sm text-cream/40 text-center py-12" data-testid="announce-mod-empty">Noch keine Ankündigungen in diesem Kanal.</p>
          ) : (
            state.messages.map((m) => (
              <div key={m.id} data-testid={`announce-mod-msg-${m.id}`} className="flex items-start justify-between gap-3 rounded-xl border border-gold/20 bg-gold/[0.07] px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold">
                    <span style={{ color: "#C69C22" }}>{m.author_rank}</span> <span className="text-cream/80">{m.author_name}</span>
                    <span className="text-cream/35 ml-2 font-normal">{fmtTime(m.created_at)}</span>
                  </p>
                  <p className="text-sm text-cream/90 whitespace-pre-wrap break-words mt-0.5 font-heading font-medium">{m.text}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`announce-mod-delete-${m.id}`} aria-label="Ankündigung löschen" className="text-cream/40 hover:text-red-400 transition-colors flex-shrink-0"><Trash2 size={15} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-cream">Ankündigung löschen?</AlertDialogTitle>
                      <AlertDialogDescription className="text-cream/60">Diese Ankündigung von {m.author_name} wird dauerhaft entfernt.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                      <AlertDialogAction data-testid={`announce-mod-delete-confirm-${m.id}`} onClick={() => removeMsg(m.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnounceModerationPanel;
